import { useState } from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { SkeletonLoader } from '@/components/atoms/SkeletonLoader';
import { EmptyState } from '@/components/molecules/EmptyState';
import { formatRelative } from '@/lib/format';
import { useMarkAllNotificheRead, useMarkNotificaRead, useNotifiche, useUnreadCount } from '@/hooks/useNotifiche';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { paths } from '@/routes/paths';

/**
 * Notification bell.
 *
 * The list is only fetched once the popover opens (`enabled` follows `open`),
 * so the bell costs a single cheap counter request on every page. The counter
 * itself is kept live by the `notification.created` socket event.
 */
export function NotificationsMenu(): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const { data: unread } = useUnreadCount();
  const { data: page, isLoading } = useNotifiche({ pageSize: 12 }, { enabled: open });
  const markRead = useMarkNotificaRead();
  const markAllRead = useMarkAllNotificheRead();

  const notifications = open ? (page?.data ?? []) : [];
  const unreadCount = unread?.count ?? 0;

  return (
    <>
      <Tooltip title="Notifiche">
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          aria-label={unreadCount > 0 ? `Notifiche, ${unreadCount} non lette` : 'Notifiche'}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsNoneOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: 'min(320px, calc(100vw - 24px))', sm: 380 },
              maxHeight: 460,
              mt: 1,
              border: '1px solid',
              borderColor: 'divider',
            },
          },
        }}
      >
        <Stack
          direction="row"
          sx={{ px: 4, py: 3, alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
        >
          <Typography variant="h6" component="h2">
            <Link
              component={RouterLink}
              to={paths.notifiche}
              color="inherit"
              underline="hover"
              onClick={() => setAnchorEl(null)}
            >
              Notifiche
            </Link>
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" variant="text" onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending}>
              Segna tutte come lette
            </Button>
          )}
        </Stack>

        <Divider />

        {isLoading ? (
          <Box sx={{ p: 4 }}>
            <SkeletonLoader variant="list" count={4} />
          </Box>
        ) : notifications.length === 0 ? (
          <EmptyState
            size="small"
            title="Nessuna notifica"
            description="Ti avviseremo qui quando cambia lo stato di un servizio o di un ticket."
          />
        ) : (
          <List disablePadding sx={{ overflowY: 'auto' }}>
            {notifications.map((notifica) => (
              <ListItemButton
                key={notifica.id}
                onClick={() => {
                  if (!notifica.letta) markRead.mutate(notifica.id);
                  if (notifica.tipo === 'backup.pending.created') {
                    setAnchorEl(null);
                    const dest = notifica.cliente_id
                      ? `${paths.admin.backup.pending}?clienteId=${notifica.cliente_id}`
                      : `${paths.admin.backup.pending}?clienteId=unassigned`;
                    void navigate(dest);
                  }
                }}
                sx={{
                  alignItems: 'flex-start',
                  gap: 2,
                  px: 4,
                  py: 3,
                  borderRadius: 0,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: notifica.letta ? 'transparent' : 'action.selected',
                }}
              >
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    mt: 1,
                    flexShrink: 0,
                    backgroundColor: notifica.letta ? 'transparent' : 'primary.main',
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: notifica.letta ? 500 : 600 }}>
                    {notifica.titolo ?? notifica.tipo}
                  </Typography>
                  {notifica.messaggio && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {notifica.messaggio}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {formatRelative(notifica.creato_il)}
                  </Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}

        <Divider />
        <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
          <Button component={RouterLink} to={paths.notifiche} size="small" onClick={() => setAnchorEl(null)}>
            Vedi tutte le notifiche
          </Button>
        </Box>
      </Popover>
    </>
  );
}
